<template>
  <div :id="chartId" style="height: 300px; width: 100%;" />
</template>

<script lang="ts">
import { PropType, defineComponent, markRaw } from 'vue';
import type * as echarts from 'echarts';

import { Nullable } from '@/utils/types';
import { IVisualizationDataItem } from '@/bundles/Visualizations/interfaces/IVisualizationDataItem';
import { loadEcharts } from '@/bundles/Visualizations/helpers/loadEcharts';
import { EchartsThemeMixin } from '@/bundles/Visualizations/mixins/EchartsThemeMixin';

interface IEChartBar {
  myChart: Nullable<echarts.ECharts>;
  isDestroyed: boolean;
}

export default defineComponent({
  name: 'EChartBar',

  mixins: [EchartsThemeMixin],

  props: {
    chartId: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    chartData: {
      type: Array as PropType<IVisualizationDataItem[]>,
      required: true
    },
  },

  data: (): IEChartBar => ({
    myChart: null,
    isDestroyed: false,
  }),

  watch: {
    echartsTheme () {
      this.myChart?.dispose();
      this.myChart = null;
      this.loadChart();
    },
    chartData: {
      handler () {
        this.loadChart();
      },
      deep: true,
    }
  },

  mounted () { // can't be moved to watch because it will be called before mounted
    this.loadChart();
  },

  beforeUnmount () {
    this.isDestroyed = true;
    this.myChart?.dispose();
    this.myChart = null;
  },

  methods: {
    async loadChart () {
      if (this.isDestroyed) {
        return;
      }

      const container = document.getElementById(this.chartId);

      if (!container) {
        return;
      }

      const echarts = await loadEcharts();

      if (this.isDestroyed) {
        return;
      }

      if (!this.myChart) {
        // Kept out of Vue's reactivity: a proxied ECharts instance breaks its internal identity checks.
        this.myChart = markRaw(echarts.init(container, this.echartsTheme));
      }

      // 120 is approximately the width of the y-axis labels
      const xLabelMaxWidth = this.chartData.length > 5 ? 1000 : (this.myChart.getWidth() - 120) / this.chartData.length;

      this.myChart.setOption({
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            data: this.chartData.map((item) => item.name),
            axisLabel: {
              interval: 0, // show all labels
              overflow: 'break',
              width: xLabelMaxWidth,
              rotate: this.chartData.length > 5 ? 45 : 0,
            },
          },
          yAxis: [
            { type: 'value' }
          ],
          series: [
            {
              name: 'Direct',
              type: 'bar',
              barWidth: '60%',
              data: this.chartData,
              tooltip: {
                valueFormatter: this.chartData.find((item) => item.tooltip?.valueFormatter)?.tooltip?.valueFormatter
              }
            }
          ]
        },
        true
      );
    }
  }
})
</script>
